import { computed } from "vue";

export type ActiveReferralCampaignResponse = {
    success: boolean;
    campaign?: {
        rewardPercentage?: number;
        maxPayoutPerUser?: number;
        minInvestmentThreshold?: number;
    };
};

const FALLBACK_MAX_PAYOUT = 10000;
const FALLBACK_MIN_INVESTMENT = 0;
const FALLBACK_REWARD_PERCENTAGE = 0.5;

function formatInr(amount: number) {
    return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
    }).format(amount);
}

export async function useActiveReferralCampaign() {
    const runtimeConfig = useRuntimeConfig();

    const { data: activeCampaignData } = await useFetch<ActiveReferralCampaignResponse>(
        `${runtimeConfig.public.USER_API_URL}/referral/campaign/active`,
        {
            key: "referral-active-campaign",
            dedupe: "defer",
        }
    );

    const maxPayoutPerUser = computed(() => {
        return activeCampaignData.value?.campaign?.maxPayoutPerUser ?? FALLBACK_MAX_PAYOUT;
    });

    const rewardPercentage = computed(() => {
        return activeCampaignData.value?.campaign?.rewardPercentage ?? FALLBACK_REWARD_PERCENTAGE;
    });

    const minInvestment = computed(() => {
        const rawMin = activeCampaignData.value?.campaign?.minInvestmentThreshold;
        if (typeof rawMin === "number" && rawMin > 0) {
            return rawMin;
        }
        return FALLBACK_MIN_INVESTMENT;
    });

    const maxRewardFormatted = computed(() => formatInr(maxPayoutPerUser.value));

    const minInvestmentFormatted = computed(() => formatInr(minInvestment.value));

    /** True when API returned an explicit min threshold (show min-investment copy). */
    const minInvestmentIsFromCampaign = computed(() => {
        const raw = activeCampaignData.value?.campaign?.minInvestmentThreshold;
        return typeof raw === "number" && raw > 0;
    });

    return {
        activeCampaignData,
        maxPayoutPerUser,
        rewardPercentage,
        minInvestment,
        maxRewardFormatted,
        minInvestmentFormatted,
        minInvestmentIsFromCampaign,
        FALLBACK_MAX_PAYOUT,
        FALLBACK_MIN_INVESTMENT,
        FALLBACK_REWARD_PERCENTAGE,
    };
}
